import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import Card from "components/ui/Card/Card";
import Alert, { type AlertVariant } from "components/ui/Alert/Alert";
import DataTable, { type DataTableColumn } from "components/ui/DataTable/DataTable";
import PaginationFooter from "components/ui/PaginationFooter/PaginationFooter";
import Icon from "components/ui/Icon/Icon";
import { normalizeRoleName, RoleName } from "constants/roles";
import { useI18n } from "context/I18nContext";
import { useAuth } from "context/AuthContext";
import {
  fetchRecharges,
  type PaginationMeta,
} from "utils/rechargesApi";
import { formatCurrency } from "utils/format";
import {
  formatDateTime,
  toDateInputValue,
  toRangeEndIso,
  toRangeStartIso,
} from "utils/dateUtils";
import styles from "./RechargePage.module.scss";

type RechargeRow = {
  id: string;
  accountNo: string;
  amount: number;
  msisdn?: string;
  transactionStatus?: string;
  transactionReference?: string;
  transactionDate?: string;
  rechargeType?: string;
  balance?: number;
};

const PAGE_SIZE = 10;
const DATE_FROM_PARAM = "fromDate";
const DATE_TO_PARAM = "toDate";

const renderNullableText = (value: unknown): string => {
  if (value === null || value === undefined) {
    return "—";
  }
  if (typeof value === "string" && value.length === 0) {
    return "—";
  }
  return String(value);
};

type FlashMessage = {
  variant: AlertVariant;
  message: string;
};

function RechargePage() {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { user, refreshUser } = useAuth();
  const location = useLocation() as ReturnType<typeof useLocation> & {
    state?: { flash?: FlashMessage };
  };
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = Math.max(1, Number(searchParams.get("page")) || 1);
  const rawSearchParam = searchParams.get("search") ?? "";
  const searchTerm = rawSearchParam.trim();
  const fromDateParam = searchParams.get(DATE_FROM_PARAM) ?? "";
  const toDateParam = searchParams.get(DATE_TO_PARAM) ?? "";
  const pumpIdParam = searchParams.get("pumpId") ?? "";

  const [recharges, setRecharges] = useState<RechargeRow[]>([]);
  const [meta, setMeta] = useState<PaginationMeta | null>(null);
  const [searchInput, setSearchInput] = useState(rawSearchParam);
  const [fromDateInput, setFromDateInput] = useState(() => toDateInputValue(fromDateParam));
  const [toDateInput, setToDateInput] = useState(() => toDateInputValue(toDateParam));
  const [pumpIdInput, setPumpIdInput] = useState(pumpIdParam);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const searchCooldownRef = useRef<number | null>(null);
  const [searchLocked, setSearchLocked] = useState(false);
  const [flash, setFlash] = useState<FlashMessage | null>(null);
  const normalizedRoles = useMemo(
    () =>
      (user?.roles ?? [])
        .map((role) => normalizeRoleName(role.roleName))
        .filter((roleName): roleName is RoleName => Boolean(roleName)),
    [user],
  );
  const isPumpOperator = normalizedRoles.includes(RoleName.PUMP_OPERATOR);
  const pumpList = useMemo(() => user?.pumps ?? [], [user?.pumps]);
  const hasPumps = pumpList.length > 0;
  const pumpFilterLabel = useMemo(() => {
    const label = t("rechargePage.filters.pump");
    return label === "rechargePage.filters.pump" ? "Pump" : label;
  }, [t]);
  const firstPumpId = pumpList[0]?.pumpId ? String(pumpList[0].pumpId) : "";
  const selectedPumpId = pumpIdInput || pumpIdParam || "";
  const filteredPumpList = useMemo(
    () =>
      selectedPumpId
        ? pumpList.filter((pump) => String(pump.pumpId) === selectedPumpId)
        : [],
    [pumpList, selectedPumpId]
  );

  const columns = useMemo<DataTableColumn<RechargeRow>[]>(
    () => [
      {
        id: "accountNo",
        header: t("rechargePage.table.accountNo"),
        accessorKey: "accountNo",
      },
      {
        id: "rechargeType",
        header: t("rechargePage.table.type"),
        accessorKey: "rechargeType",
        cell: (value) => (
          <span>
            {typeof value === "string" && value.length
              ? value
              : t("rechargePage.unknownStatus")}
          </span>
        ),
      },
      {
        id: "transactionReference",
        header: t("rechargePage.table.reference"),
        accessorKey: "transactionReference",
        cell: (value) => <span>{renderNullableText(value)}</span>,
      },
      {
        id: "transactionDate",
        header: t("rechargePage.table.date"),
        accessorKey: "transactionDate",
        cell: (value) => <span>{formatDateTime(value)}</span>,
      },
      {
        id: "amount",
        header: t("rechargePage.table.amount"),
        accessorKey: "amount",
        cell: (value) => (
          <span>{formatCurrency(Number(value ?? 0), "৳")}</span>
        ),
        align: "right",
      },
      {
        id: "balance",
        header: t("rechargePage.table.balance"),
        accessorKey: "balance",
        cell: (value) => <span>{formatCurrency(Number(value ?? 0), "৳")}</span>,
        align: "right",
      }
    ],
    [t]
  );

  useEffect(() => {
    if (location.state?.flash) {
      setFlash(location.state.flash);
      navigate(
        { pathname: ".", search: location.search },
        { replace: true, state: null }
      );
    }
  }, [location, navigate]);

  useEffect(() => {
    if (!flash) {
      return;
    }
    const timer = window.setTimeout(() => setFlash(null), 4000);
    return () => window.clearTimeout(timer);
  }, [flash]);

  useEffect(() => {
    setSearchInput(rawSearchParam);
  }, [rawSearchParam]);

  useEffect(() => {
    setFromDateInput(toDateInputValue(fromDateParam));
  }, [fromDateParam]);

  useEffect(() => {
    setToDateInput(toDateInputValue(toDateParam));
  }, [toDateParam]);

  useEffect(() => {
    if (pumpIdParam) {
      setPumpIdInput(pumpIdParam);
      return;
    }
    if (!pumpIdInput && firstPumpId) {
      setPumpIdInput(firstPumpId);
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set("pumpId", firstPumpId);
      nextParams.set("page", "1");
      setSearchParams(nextParams, { replace: false });
    }
  }, [pumpIdParam, firstPumpId, pumpIdInput, searchParams, setSearchParams]);

  useEffect(
    () => () => {
      if (searchCooldownRef.current) {
        window.clearTimeout(searchCooldownRef.current);
      }
    },
    []
  );

  useEffect(() => {
    let cancelled = false;
    const syncPumpData = async () => {
      try {
        await refreshUser();
        if (!cancelled) {
          setReloadKey((prev) => prev + 1);
        }
      } catch {
        // ignore background refresh failures
      }
    };

    syncPumpData();
    const handleFocus = () => syncPumpData();
    const handleVisibility = () => {
      if (document.visibilityState === "visible") {
        syncPumpData();
      }
    };
    window.addEventListener("focus", handleFocus);
    document.addEventListener("visibilitychange", handleVisibility);
    return () => {
      cancelled = true;
      window.removeEventListener("focus", handleFocus);
      document.removeEventListener("visibilitychange", handleVisibility);
    };
  }, [refreshUser]);

  useEffect(() => {
    const controller = new AbortController();
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const pumpIdNumber = selectedPumpId ? Number(selectedPumpId) : undefined;
        const resolvedPumpId = Number.isFinite(pumpIdNumber) ? pumpIdNumber : undefined;
        const response = await fetchRecharges({
          page: pageParam,
          pageSize: PAGE_SIZE,
          search: searchTerm || undefined,
          fromDate: fromDateParam || undefined,
          toDate: toDateParam || undefined,
          pumpId: resolvedPumpId,
          signal: controller.signal,
        });
        setRecharges(
          response.items.map((item) => ({
            id: String(item.id),
            accountNo: item.accountNo ?? "—",
            amount: Number(item.amount ?? 0),
            balance: Number(item.balance ?? 0),
            transactionStatus: item.transactionStatus ?? undefined,
            transactionReference: item.transactionReference ?? undefined,
            transactionDate: item.transactionDate ?? undefined,
            rechargeType: item.rechargeType ?? undefined,
          }))
        );
        setMeta(response.meta);
      } catch (err) {
        if ((err as DOMException).name === "AbortError") {
          return;
        }
        setError((err as Error).message || t("rechargePage.error"));
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [pageParam, searchTerm, fromDateParam, toDateParam, t, reloadKey, selectedPumpId]);

  const totalPages = meta?.pages ?? (recharges.length > 0 ? 1 : 0);
  const currentPage = meta?.page ?? pageParam;

  const handlePageChange = (nextPage: number) => {
    if (nextPage === currentPage) {
      return;
    }
    const safeMax = Math.max(totalPages, 1);
    if (nextPage < 1 || nextPage > safeMax) {
      return;
    }
    const next = new URLSearchParams(searchParams);
    next.set("page", String(nextPage));
    setSearchParams(next, { replace: false });
  };

  const applyPumpFilter = (nextPumpId: string) => {
    const resolved = nextPumpId || firstPumpId;
    if (!resolved) {
      return;
    }
    setPumpIdInput(resolved);
    const next = new URLSearchParams(searchParams);
    next.set("pumpId", resolved);
    next.set("page", "1");
    setSearchParams(next, { replace: false });
  };

  const handleSearchSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (searchLocked) {
      return;
    }
    setSearchLocked(true);
    if (searchCooldownRef.current) {
      window.clearTimeout(searchCooldownRef.current);
    }
    searchCooldownRef.current = window.setTimeout(() => setSearchLocked(false), 3000);

    const trimmed = searchInput.trim();
    const nextFromParam = toRangeStartIso(fromDateInput);
    const nextToParam = toRangeEndIso(toDateInput);
    if (
      trimmed === searchTerm &&
      (nextFromParam ?? "") === fromDateParam &&
      (nextToParam ?? "") === toDateParam &&
      (pumpIdInput || "") === pumpIdParam
    ) {
      setReloadKey((prev) => prev + 1);
      return;
    }
    const next = new URLSearchParams(searchParams);
    if (trimmed) {
      next.set("search", trimmed);
    } else {
      next.delete("search");
    }
    if (nextFromParam) {
      next.set(DATE_FROM_PARAM, nextFromParam);
    } else {
      next.delete(DATE_FROM_PARAM);
    }
    if (nextToParam) {
      next.set(DATE_TO_PARAM, nextToParam);
    } else {
      next.delete(DATE_TO_PARAM);
    }
    if (pumpIdInput) {
      next.set("pumpId", pumpIdInput);
    } else {
      next.delete("pumpId");
    }
    next.set("page", "1");
    setSearchParams(next, { replace: false });
  };

  return (
    <div className={styles.page}>
      <Card>
        <div className={styles.header}>
          <div>
            <h1 className={styles.title}>{t("rechargePage.title")}</h1>
            <p className={styles.subtitle}>{t("rechargePage.subtitle")}</p>
          </div>
          {isPumpOperator && (
            <button
              type="button"
              className={styles.primaryButton}
              onClick={() =>
                navigate("/recharge/new", {
                  state: {
                    from: { pathname: "/recharge", search: location.search },
                  },
                })
              }
            >
              <Icon name="add" color="inherit" />
              {t("rechargePage.add")}
            </button>
          )}
        </div>

        {hasPumps && (
          <section className={styles.pumpSection} aria-label={t("rechargePage.pumpSummary.title")}>
            <div className={styles.pumpHeader}>
              <div>
                <h3>{t("rechargePage.pumpSummary.title")}</h3>
                <span className={styles.pumpHint}>
                  {t("rechargePage.pumpSummary.subtitle")}
                </span>
              </div>
            </div>
            <div className={styles.pumpGrid}>
              {filteredPumpList.map((pump) => {
                const numericBalance = Number(pump.pumpBalance ?? 0);
                const isDepleted = numericBalance <= 0;
                const isSelected = String(pump.pumpId) === selectedPumpId;
                return (
                  <button
                    type="button"
                    key={pump.pumpId}
                    className={styles.pumpCard}
                    data-depleted={isDepleted || undefined}
                    data-selected={isSelected || undefined}
                    onClick={() => applyPumpFilter(String(pump.pumpId))}
                  >
                    <div className={styles.pumpName}>{pump.pumpName}</div>
                    <div className={styles.pumpZone}>{pump.zoneName}</div>
                    <div className={styles.pumpBalanceLabel}>
                      {t("rechargePage.pumpSummary.balance")}
                    </div>
                    <div
                      className={`${styles.pumpBalance} ${
                        isDepleted ? styles.pumpBalanceEmpty : ""
                      }`}
                    >
                      {formatCurrency(numericBalance, "৳")}
                    </div>
                  </button>
                );
              })}
            </div>
          </section>
        )}

        <form className={styles.searchBar} onSubmit={handleSearchSubmit}>
          <label className={styles.fieldGroup} htmlFor="recharge-search">
            <span className={styles.fieldLabel}>{t("rechargePage.searchLabel")}</span>
            <input
              id="recharge-search"
              className={styles.searchInput}
              type="search"
              placeholder={t("rechargePage.searchPlaceholder")}
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
            />
          </label>
          <label className={styles.fieldGroup} htmlFor="recharge-from-date">
            <span className={styles.fieldLabel}>{t("rechargePage.filters.from")}</span>
            <input
              id="recharge-from-date"
              className={styles.dateInput}
              type="date"
              value={fromDateInput}
              max={toDateInput || undefined}
              onChange={(event) => setFromDateInput(event.target.value)}
            />
          </label>
          <label className={styles.fieldGroup} htmlFor="recharge-to-date">
            <span className={styles.fieldLabel}>{t("rechargePage.filters.to")}</span>
            <input
              id="recharge-to-date"
              className={styles.dateInput}
              type="date"
              value={toDateInput}
              min={fromDateInput || undefined}
              onChange={(event) => setToDateInput(event.target.value)}
            />
          </label>
          {hasPumps && (
            <label className={styles.fieldGroup} htmlFor="recharge-pump">
              <span className={styles.fieldLabel}>{pumpFilterLabel}</span>
              <select
                id="recharge-pump"
                className={styles.select}
                value={selectedPumpId}
                onChange={(event) => applyPumpFilter(event.target.value)}
              >
                {pumpList.map((pump) => (
                  <option key={pump.pumpId} value={pump.pumpId}>
                    {pump.pumpName}
                  </option>
                ))}
              </select>
            </label>
          )}
          <button type="submit" className={styles.searchButton} disabled={searchLocked}>
            {t("datatable.search")}
          </button>
        </form>

        {(flash || error) && (
          <div className={styles.alertStack}>
            {flash && (
              <Alert
                variant={flash.variant}
                dismissible
                onClose={() => setFlash(null)}
              >
                {flash.message}
              </Alert>
            )}
            {error && (
              <Alert variant="error" dismissible onClose={() => setError(null)}>
                {error}
              </Alert>
            )}
          </div>
        )}

        <DataTable
          columns={columns}
          data={recharges}
          globalSearch={false}
          loading={loading}
          loadingText={t("rechargePage.loading")}
          emptyState={
            loading
              ? t("rechargePage.loading")
              : error
                ? t("rechargePage.error")
                : t("rechargePage.empty")
          }
          manualPagination={
            meta
              ? {
                  pageIndex: Math.max((meta.page ?? 1) - 1, 0),
                  pageCount: Math.max(meta.pages ?? 1, 1),
                  pageSize: meta.pageSize ?? PAGE_SIZE,
                  totalItems: meta.total ?? recharges.length,
                  onPageChange: (nextIndex) => handlePageChange(nextIndex + 1),
                }
              : undefined
          }
          showPaginationInfo={false}
          showPaginationControls={false}
        />

        {meta && (
          <PaginationFooter
            totalLabel={t("rechargePage.meta.total")}
            pageLabel={t("rechargePage.meta.page")}
            pageSizeLabel={t("rechargePage.meta.pageSize")}
            total={meta.total ?? 0}
            page={meta.page ?? 1}
            pageCount={Math.max(meta.pages ?? 1, 1)}
            pageSize={meta.pageSize ?? PAGE_SIZE}
            previousLabel={t("datatable.previous")}
            nextLabel={t("datatable.next")}
            goToLabel={t("rechargePage.pagination.goToLabel")}
            goButtonLabel={t("rechargePage.pagination.goButton")}
            invalidMessage={(max) => t("rechargePage.pagination.invalidPage", { max })}
            onPageChange={handlePageChange}
          />
        )}
      </Card>
    </div>
  );
}

export default RechargePage;
